import {
	observable,
	action,
	computed,
	reaction,
	IReactionDisposer,
} from "mobx";
import { Subscription } from "rxjs";
import { getSecondaryOfferColumns } from "@gp/components";
import {
	MainOfferStore,
	SportOffer,
	EventOffer,
	LiveSportStructureOffer,
	getEventSortByFavorite,
} from "@gp/offer";
import {
	EventType,
	LiveStatus,
	LiveStreamStatus,
	OfferSubscriptionResponse,
} from "@gp/models";
import { ConsoleLogger, LogLevel, Sorter } from "@gp/utility";
import { ISubscriptionRequest, EventFilterParams } from "@gp/hub";
import betRules from "@betting-rules";
import RootStore from "@state/stores/RootStore";
import { LiveOfferMenuStore } from "@state/stores/offer/live/LiveOfferMenuStore";

import { BettingTypeSelectorsStore } from "../shared/BettingTypeSelectorsStore";
import { LoaderStore } from "@state/stores/common";
import {
	TFlatVirtualizedSportItem,
	TGropedByTournamentItem,
} from "@data-types/offer/live/TFlatVirtualizedSportItem";
import { VirtualizedListItemType } from "@data-types/offer/live/VirtualizedListItemTypeEnum";
import { SessionStorageProvider } from "@state";
import { TSection } from "@data-types/offer/live/Section";

export type TRootStore = {
	hub: RootStore["hub"];
};

class DefaultFilter {
	eventType: EventType;
	liveStatus: LiveStatus;

	constructor() {
		this.eventType = EventType.NORMAL;
		this.liveStatus = LiveStatus.LIVE;
	}
}

export default class LiveViewStore extends MainOfferStore {
	protected _liveSubscriptionCounter: number = 0;
	protected rootStore: TRootStore;
	protected liveOfferMenuStore: LiveOfferMenuStore;
	private bettingTypes: any;
	private loader: LoaderStore;

	//#region disposers
	private whenHubStartedDisposer: IReactionDisposer | null = null;
	private subscription: Subscription | null = null;

	//#endregion disposers

	//#region  observable

	@observable protected filter = new DefaultFilter();
	@observable protected numberOfColumns: number = 4;
	@action.bound
	public setNumberOfColumns(newNumber: number): void {
		if (newNumber === this.numberOfColumns) {
			return;
		}
		this.numberOfColumns = newNumber;
	}

	@computed public get period() {
		return this.liveOfferMenuStore.periodSubMenu.selectedPeriodKey;
	}

	@computed get isFavoritesReady() {
		if (
			![
				TSection.MyFavoritesEvents,
				TSection.MyFavoritesTournaments,
			].includes(this.period)
		) {
			return true;
		}
		return this.liveOfferMenuStore.favoritesStore.isStoreInitialized;
	}

	@observable public isStoreInitialized = false;
	@observable public isGroupedByTournament = false;
	@observable public isFetchingData = false;
	@observable public onSelectedBettingType: string;

	@observable protected eventsWithErrors: Array<string> = [];

	//#endregion observable

	//#region  computed

	@computed public get selectedBettingType() {
		return this.onSelectedBettingType;
	}

	@computed public get isLive() {
		if (
			[
				TSection.Matches,
				TSection.MyFavoritesEvents,
				TSection.MyFavoritesTournaments,
			].includes(this.period)
		) {
			return true;
		}
		return false;
	}

	@computed public get isUpcoming() {
		return this.period === TSection.Upcoming;
	}

	@computed public get bettingTypesByAbrv() {
		let btArr = [...this.bettingTypes.values()];

		return btArr.reduce((acc, item) => {
			acc[item.abrv] = item;
			return acc;
		}, {});
	}

	@computed public get isLoading() {
		return this.loader.isLoading;
	}

	@computed public get isEmpty() {
		return (
			this.virtualizedLiveSportsList.length === 0 &&
			!this.isFetchingData &&
			this.isStoreInitialized
		);
	}

	@computed public get eventsToDisplayIsEmpty() {
		return this.isEmpty;
	}

	@computed public get eventIdsForEventSwitcher() {
		const list: string[] = [];

		this.eventsInSports.sports.forEach((sport) => {
			this.filterSportEvents(sport)?.forEach((event) =>
				list.push(event.id)
			);
		});

		return list;
	}

	/**
	 * Returns favorite events
	 */
	@computed get activeFavorites() {
		return [...this.liveOfferMenuStore.favoritesStore.userFavoriteEventsSet]
			.map((feId) => this.eventsMap.get(feId))
			.filter(<T>(item: T | undefined): item is T => item != null);
	}

	/**
	 * Returns number of favorite events that are currently in active offer
	 */
	@computed get activeFavoritesCount() {
		return this.activeFavorites.length;
	}

	@computed get activeFavoriteTournamentIds() {
		const ids = new Set<string>();
		for (const event of Array.from(this.eventsMap.values())) {
			if (
				this.liveOfferMenuStore.favoritesStore.userFavoriteTournamentsSet.has(
					event.tournamentId
				)
			) {
				ids.add(event.tournamentId);
			}
		}
		return Array.from(ids);
	}

	@computed public get totalEventCountEmpty() {
		return this.eventsInSports.eventCount === 0;
	}

	//#endregion computed

	constructor(
		rootStore: TRootStore,
		liveOfferMenuStore: LiveOfferMenuStore,
		bettingTypeSelectorStore?: BettingTypeSelectorsStore
	) {
		super({
			logger: new ConsoleLogger(false, LogLevel.ERROR),
			removeDelay: 10,
			throttle: 4,
			enableThrottling: true,
			bettingTypeSelectorsStore:
				bettingTypeSelectorStore || new BettingTypeSelectorsStore(),
			customConfiguration: betRules,
		});

		this.rootStore = rootStore;
		this.liveOfferMenuStore = liveOfferMenuStore;
		this.loader = new LoaderStore();

		const sessionItem = SessionStorageProvider.get(
			"isLiveGroupedByTournament"
		);
		this.isGroupedByTournament =
			sessionItem != null && sessionItem === "true";
	}

	//#region  fetching data

	@action.bound
	public onInitialize() {
		this.loader.suspend();
		this.isFetchingData = true;

		this.disposeWhenHubStarted();
		this.whenHubStartedDisposer = reaction(
			() => ({
				period: this.liveOfferMenuStore.periodSubMenu.selectedPeriod
					.period,
				isMenuStoreInitialized:
					this.liveOfferMenuStore.isStoreInitialized,
				isFavoritesReady: this.isFavoritesReady,
			}),
			({ isMenuStoreInitialized, isFavoritesReady }) => {
				if (isMenuStoreInitialized && isFavoritesReady) {
					if (!this.isFetchingData) {
						this.loader.suspend();
						this.isFetchingData = true;
					}

					this.fetchData();
				}
			},
			{
				fireImmediately: true,
			}
		);
	}

	@action.bound
	private fetchData() {
		this.disposeSubscription();

		const subscriptionRequest = this.generateSubscriptionRequest();

		if (!subscriptionRequest) {
			console.error(
				`filter can not have empty id while requesting offer id subscription. Selected period: ${this.period} that has given an empty array `
			);
			this.onDoneFetching();
			return;
		}
		this.subscription = this.rootStore.hub
			.getOfferSubscription(subscriptionRequest)
			.subscribe({
				next: this.handleOfferAssign,
				error: (err: any) => {
					console.error(err);
					this.onDoneFetching();
				},
			});
	}

	@action.bound
	protected handleOfferAssign(response: OfferSubscriptionResponse) {
		this.assignOfferData(response);
		this.onDoneFetching();
	}
	//#region subscription object

	protected generateSubscriptionRequest(): ISubscriptionRequest | null {
		let offerFilter = this.generateSubscriptionOfferFilter(
			this.period === "upcoming" ? "prematch" : "live"
		);

		const eventFilter = this.getEventFilters();
		if (!eventFilter) {
			return null;
		}
		// Use this to discard data from previous subscriptions
		this._liveSubscriptionCounter++;
		return {
			subscriptionId: `live-${this._liveSubscriptionCounter}`,
			compress: WEBPACK_OFFER_COMPRESS,
			channels: [
				{
					name: "event",
					filter: eventFilter,
				},
				{
					name: "betOffer",
					filter: offerFilter,
				},
			],
		};
	}

	protected getEventFilters = (): EventFilterParams | null => {
		let filter: EventFilterParams = {};

		switch (this.period) {
			case TSection.Matches:
				filter = {
					liveStatus: LiveStatus.LIVE,
				};
				break;

			case TSection.Upcoming:
				filter = {
					liveStatus: LiveStatus.PREMATCH,
					isUpcoming: true,
				};
				break;

			case TSection.MyFavoritesEvents:
				if (
					this.liveOfferMenuStore.favoritesStore.userFavoriteEventsSet
						.size == 0
				) {
					return null;
				}
				filter = {
					liveStatus: LiveStatus.LIVE,
					id: {
						eq: [
							...this.liveOfferMenuStore.favoritesStore
								.userFavoriteEventsSet,
						],
					},
				};
				break;

			case TSection.MyFavoritesTournaments:
				if (
					this.liveOfferMenuStore.favoritesStore
						.userFavoriteTournamentsSet.size == 0
				) {
					return null;
				}
				filter = {
					liveStatus: LiveStatus.LIVE,
					tournamentId: {
						eq: [
							...this.liveOfferMenuStore.favoritesStore
								.userFavoriteTournamentsSet,
						],
					},
				};
				break;

			case TSection.PlayedEvents:
				if (
					this.liveOfferMenuStore.liveEventsFromMyBets.eventIds
						.length == 0
				) {
					return null;
				}
				filter = {
					liveStatus: LiveStatus.LIVE,
					id: {
						eq: this.liveOfferMenuStore.liveEventsFromMyBets
							.eventIds,
					},
				};
				break;

			case TSection.Highlights:
				filter = {
					liveStatus: LiveStatus.LIVE,
					isTopEvent: true,
				};
				break;
			case TSection.LiveStream:
				filter = {
					liveStatus: LiveStatus.LIVE,
					liveStreamStatus: {
						eq: [LiveStreamStatus.Live, LiveStreamStatus.Upcoming],
					},
				};
				break;

			default:
				filter = {
					liveStatus: LiveStatus.LIVE,
				};
				break;
		}

		return filter;
	};

	protected generateSubscriptionOfferFilter(eventType: "prematch" | "live") {
		const bts = this.OfferMapper.getBettingTypes(eventType, betRules);
		return [
			{
				bettingType: {
					abrv: {
						eq: bts.normal,
					},
				},
			},
			{
				bettingType: {
					abrv: {
						eq: bts.marginal,
					},
				},
				isFavorite: true,
			},
		];
	}

	//#endregion subscription object

	@action.bound
	protected onDoneFetching() {
		this.loader.resume();
		this.initialized = false;
		this.isFetchingData = false;
		this.isStoreInitialized = true;
	}

	//#endregion fetching data

	//#region disposers

	@action.bound
	public onDispose() {
		this.disposeSubscription();
		this.disposeWhenHubStarted();

		this.isStoreInitialized = false;
		this._liveSubscriptionCounter = 0;

		this.onReset();
	}

	@action.bound
	protected disposeSubscription() {
		this.eventsWithErrors = [];
		if (this.subscription != null) {
			this.subscription.unsubscribe();
			this.subscription = null;
		}
	}

	@action.bound
	protected disposeWhenHubStarted() {
		if (this.whenHubStartedDisposer != null) {
			this.whenHubStartedDisposer();
			this.whenHubStartedDisposer = null;
		}
	}

	@action.bound
	protected onReset() {
		this.reset();

		this.eventsInSports = new LiveSportStructureOffer();
	}

	//#endregion disposers

	//#region  setters

	@action.bound
	setSelectedBettingType(value: string) {
		this.onSelectedBettingType = value;
	}

	@action.bound
	setIsLiveOfferGrouped(value: boolean) {
		SessionStorageProvider.set(
			"isLiveGroupedByTournament",
			JSON.stringify(value)
		);
		this.isGroupedByTournament = value;
	}

	@action.bound
	eventFavoritesFilter = (eventId: string) => {
		return this.liveOfferMenuStore.favoritesStore.userFavoriteEventsSet.has(
			eventId
		);
	};

	@action.bound
	public registerEventWithError(eventId: string) {
		if (this.eventsWithErrors.includes(eventId)) {
			return;
		}
		this.eventsWithErrors.push(eventId);
	}

	//#endregion setters

	//#region sport collapse

	@computed public get collapsedSports(): string[] {
		return Array.from(
			this.liveOfferMenuStore.collapsedSportsStore.elements
		);
	}

	@action.bound
	public expandSport(sportId: string) {
		this.liveOfferMenuStore.expandSport(sportId);
	}

	@action.bound
	public collapseSport(sportId: string) {
		this.liveOfferMenuStore.collapseSport(sportId);
	}

	@action.bound
	public toggleSportCollapse(sportId: string) {
		this.liveOfferMenuStore.toggleSportCollapse(sportId);
	}

	//#endregion sport collapse

	//#region tournament collapse

	@computed public get collapsedTournaments(): string[] {
		return Array.from(
			this.liveOfferMenuStore.collapsedTournamentsStore.elements
		);
	}

	@action.bound
	public expandTournament(tournamentId: string) {
		this.liveOfferMenuStore.collapsedTournamentsStore.remove(tournamentId);
	}

	@action.bound
	public collapseTournament(tournamentId: string) {
		this.liveOfferMenuStore.collapsedTournamentsStore.add(tournamentId);
	}

	@action.bound
	public toggleTournamentCollapse(tournamentId: string) {
		this.liveOfferMenuStore.collapsedTournamentsStore.toggleCollapse(
			tournamentId
		);
	}

	@action.bound
	public collapseAllTournaments(tournamentIds: string[]) {
		this.liveOfferMenuStore.collapseAllTournaments(tournamentIds);
	}

	@action.bound
	public expandAllTournaments(tournamentIds: string[]) {
		this.liveOfferMenuStore.expandAllTournaments(tournamentIds);
	}

	//#endregion tournament collapse

	//#region flat sports

	@computed
	get groupedEventsBySports() {
		return this.eventsInSports.sports.reduce<TFlatVirtualizedSportItem[][]>(
			(acc, sp: SportOffer) => {
				const flatSport: TFlatVirtualizedSportItem = {
					//@ts-expect-error
					type: VirtualizedListItemType.sport,
					sport: sp,
					eventCount: 0,
					event: null,
				};

				const baseEvents = this.filterSportEvents(sp)
					?.map<TFlatVirtualizedSportItem>((event) => {
						return {
							type: VirtualizedListItemType.primaryEvent,
							sport: null,
							event,
							secondaryCount: 0,
						};
					})
					.reduce<TFlatVirtualizedSportItem[]>((acc, item) => {
						if (item.sport != null || item.isSecondary) {
							return acc;
						}

						acc.push(item);

						const eventOffer = this.eventKeysMap.get(
							item.event?.id || ""
						);
						const eventKeys = Array.from(
							eventOffer?.values() || []
						);
						if (eventKeys != null) {
							const bettingTypeSelector =
								this.configuration.bettingTypeSelectorsStore.getSportSelector(
									sp
								);

							if (bettingTypeSelector == null) {
								console.error(
									"Expected betting type slector, got null."
								);
							} else {
								const secondaryColumns =
									getSecondaryOfferColumns(
										eventKeys,
										bettingTypeSelector,
										this.numberOfColumns
									);

								if (
									!secondaryColumns.isEmpty &&
									"secondaryCount" in item
								) {
									item.secondaryCount =
										secondaryColumns.nonEmptyColumns;
									acc.push({
										type: VirtualizedListItemType.secondaryEvent,
										event: item.event,
										sport: null,
										secondaryOffer: secondaryColumns,
										isSecondary: true,
									});
								}
							}
						}

						return acc;
					}, []);

				if (baseEvents == null || baseEvents.length <= 0) {
					return acc;
				}
				//@ts-expect-error
				flatSport.eventCount =
					baseEvents?.filter((e) => !e.isSecondary).length || 0;

				if (
					this.liveOfferMenuStore.collapsedSportsStore.elements.has(
						sp.id
					)
				) {
					acc.push([flatSport]);
				} else {
					baseEvents.unshift(flatSport);
					acc.push(baseEvents);
				}

				return acc;
			},
			[]
		);
	}

	@computed
	public get virtualizedLiveSportsList() {
		return this.isGroupedByTournament
			? this.groupedEventsByTournament
			: this.groupedEventsBySports;
	}

	get groupedEventsByTournament() {
		return this.groupedEventsBySports.map((sport) => {
			const mappedSport: TFlatVirtualizedSportItem[] = [];

			mappedSport.push(sport[0]);

			const groupedByTournament: {
				[key: string]: TGropedByTournamentItem;
			} = {};

			for (const row of sport) {
				const tournamentId = row.event?.tournamentId;

				if (tournamentId && !groupedByTournament[tournamentId]) {
					groupedByTournament[tournamentId] = {
						type: VirtualizedListItemType.groupByTournament,
						categoryId: row.event?.sportCategoryId,
						tournament: tournamentId,
						events: [],
					};
				}

				if (
					tournamentId &&
					!this.liveOfferMenuStore.collapsedTournamentsStore.elements.has(
						tournamentId
					)
				) {
					groupedByTournament[tournamentId].events.push(row);
				}
			}

			for (const tournamentId in groupedByTournament) {
				const {
					categoryId,
					tournament: tid,
					events,
				} = groupedByTournament[tournamentId];
				mappedSport.push({
					categoryId,
					tournament: tid,
					events,
					type: VirtualizedListItemType.groupByTournament,
				});
				mappedSport.push(...events);
			}
			return mappedSport;
		});
	}

	protected filterSportEvents(sport: SportOffer): EventOffer[] | undefined {
		if (this.period === TSection.MyFavoritesEvents) {
			return sport.events?.filter(({ id }) =>
				this.liveOfferMenuStore.favoritesStore.userFavoriteEventsSet.has(
					id
				)
			);
		}

		if (this.period === TSection.MyFavoritesTournaments) {
			return sport.events?.filter(({ tournamentId }) =>
				this.liveOfferMenuStore.favoritesStore.userFavoriteTournamentsSet.has(
					tournamentId
				)
			);
		}

		if (
			this.liveOfferMenuStore.menu.checkState === 0 &&
			// This is live offer menu magic
			// If we are on live events and there is sport selected in side menu, but all is deselected
			// Then display that sport, if nothing is selected in sport menu for that sport then display all from sport
			// Else display what is selected
			(this.period !== TSection.Matches ||
				(this.period === TSection.Matches &&
					(this.liveOfferMenuStore.selectedSport?.node.id ===
						sport.id ||
						this.liveOfferMenuStore.selectedSport == null)))
		) {
			if (!sport.events) {
				return sport.events;
			}

			if (
				this.liveOfferMenuStore.favoritesStore.userFavoriteEventsSet
					.size > 0
			) {
				return sport.events?.sort(
					Sorter.sort((a, b) => {
						return Sorter.sort((a1: EventOffer, b1: EventOffer) =>
							getEventSortByFavorite(
								a1,
								b1,
								this.liveOfferMenuStore.favoritesStore
							)
						)(a, b);
					})
				);
			}

			return sport.events;
		}

		const sportInMenu = this.liveOfferMenuStore.menu.children.find(
			({ node: { id } }) => id === sport.id
		);

		if (sportInMenu == null) {
			// Sport in menu was removed or not added yet
			return [];
		}

		const sportSelectedTournaments = sportInMenu.checkedLeafNodeIds;

		return sport.events
			?.sort(
				Sorter.sort((a, b) => {
					return Sorter.sort((a1: EventOffer, b1: EventOffer) =>
						getEventSortByFavorite(
							a1,
							b1,
							this.liveOfferMenuStore.favoritesStore
						)
					)(a, b);
				})
			)
			.filter((event) =>
				sportSelectedTournaments.includes(event.tournamentId)
			);
	}

	//#endregion flat sports
}
